/**
 * Admin Game Sync Page
 *
 * Synchronises the PlayXworld game catalog with wePAY via the new Firebase
 * Cloud Function callables (asia-southeast1). Replaces the legacy tRPC
 * `wepaySync.*` mutations entirely.
 *
 * UX patterns adopted from the legacy PlusGame backoffice:
 *   - "Pending updates" badge that shows the count of differences between
 *     wePAY and Firestore
 *   - Diff modal that previews every change before any write
 *   - Cardinal copy: new games/packages default to disabled
 *
 * Brutalist styling (thick black borders, monochrome, mono technical labels).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import SyncPreviewModal from '@/components/wepay/SyncPreviewModal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  Loader2,
  Bell,
  Plus,
  PlayCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  collection,
  getDocs,
  getFirestore,
  type DocumentData,
} from 'firebase/firestore';

import { getFirebaseApp } from '@/lib/firebase';
import {
  wepayCatalogPreview,
  wepayCatalogSync,
  wepaySyncHistory,
  wepaySyncStatus,
  wepaySeedCompanies,
  wepayCreateMockTopup,
  diffPendingCount,
  timestampToDate,
  type CatalogDiff,
  type SyncRun,
  type SyncStatusResponse,
  type CreateMockTopupResponse,
} from '@/lib/wepayFunctions';
import { emptyDiff } from '@/lib/wepayDiff';

interface CompanyDoc {
  code: string;
  type: string;
  gameKey: string;
  enabled: boolean;
}

function formatDate(value: SyncRun['startedAt'] | string | null | undefined): string {
  const d = timestampToDate(value);
  if (!d) return '—';
  return format(d, 'MMM dd, yyyy HH:mm:ss');
}

function formatDuration(ms: number | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export default function AdminGameSync() {
  // ---------------------------------------------------------------------
  // Status + history
  // ---------------------------------------------------------------------
  const [status, setStatus] = useState<SyncStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState<boolean>(true);
  const [history, setHistory] = useState<SyncRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(true);

  // ---------------------------------------------------------------------
  // Diff / preview
  // ---------------------------------------------------------------------
  const [diff, setDiff] = useState<CatalogDiff | null>(null);
  const [previewErrors, setPreviewErrors] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);

  // ---------------------------------------------------------------------
  // Companies list (for the mock top-up form)
  // ---------------------------------------------------------------------
  const [companies, setCompanies] = useState<CompanyDoc[]>([]);
  const [seeding, setSeeding] = useState<boolean>(false);

  // ---------------------------------------------------------------------
  // Sync history details modal
  // ---------------------------------------------------------------------
  const [selectedRun, setSelectedRun] = useState<SyncRun | null>(null);

  // ---------------------------------------------------------------------
  // Mock top-up form
  // ---------------------------------------------------------------------
  const [mockOpen, setMockOpen] = useState<boolean>(false);
  const [mockSubmitting, setMockSubmitting] = useState<boolean>(false);
  const [mockResult, setMockResult] = useState<CreateMockTopupResponse | null>(
    null,
  );
  const [mockCompany, setMockCompany] = useState<string>('');
  const [mockAmount, setMockAmount] = useState<string>('100');
  const [mockRef1, setMockRef1] = useState<string>('PLAYER123');
  const [mockAutoCallback, setMockAutoCallback] = useState<boolean>(true);
  const [mockSucceed, setMockSucceed] = useState<boolean>(true);

  // =====================================================================
  // Loaders
  // =====================================================================

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const s = await wepaySyncStatus();
      setStatus(s);
    } catch (err) {
      console.error('[AdminGameSync] loadStatus failed:', err);
      toast.error(
        `Failed to load sync status: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const h = await wepaySyncHistory({ limit: 50 });
      setHistory(h.runs);
    } catch (err) {
      console.error('[AdminGameSync] loadHistory failed:', err);
      toast.error(
        `Failed to load sync history: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const app = getFirebaseApp();
      if (!app) return;
      const db = getFirestore(app);
      const snap = await getDocs(collection(db, 'wepay_companies'));
      const out: CompanyDoc[] = snap.docs.map((d) => {
        const data = d.data() as DocumentData;
        return {
          code: String(data.code ?? d.id),
          type: String(data.type ?? 'cashcard'),
          gameKey: String(data.gameKey ?? d.id),
          enabled: data.enabled !== false,
        };
      });
      setCompanies(out);
      if (!mockCompany && out.length > 0) {
        setMockCompany(out[0].code);
      }
    } catch (err) {
      console.error('[AdminGameSync] loadCompanies failed:', err);
    }
  }, [mockCompany]);

  useEffect(() => {
    void loadStatus();
    void loadHistory();
    void loadCompanies();
  }, [loadStatus, loadHistory, loadCompanies]);

  // =====================================================================
  // Actions
  // =====================================================================

  const detectUpdates = useCallback(async () => {
    if (previewing) return;
    setPreviewing(true);
    try {
      const r = await wepayCatalogPreview();
      setDiff(r.diff);
      setPreviewErrors(r.errors);
      const total = diffPendingCount(r.diff);
      if (total === 0) {
        toast.success('ไม่มีการเปลี่ยนแปลง — ข้อมูลตรงกับ wePAY แล้ว');
      } else {
        toast.success(`พบการเปลี่ยนแปลง ${total} รายการ`);
      }
      return r;
    } catch (err) {
      console.error('[AdminGameSync] preview failed:', err);
      toast.error(
        `Detect Updates failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      setDiff(null);
      return null;
    } finally {
      setPreviewing(false);
    }
  }, [previewing]);

  const handleSyncNow = useCallback(async () => {
    if (previewing || applying) return;
    const r = await detectUpdates();
    if (!r) return;
    setShowPreview(true);
  }, [detectUpdates, previewing, applying]);

  const handleApplySync = useCallback(async () => {
    if (applying) return;
    setApplying(true);
    try {
      const result = await wepayCatalogSync();
      if (result.success) {
        toast.success(
          `Sync OK — +${result.gamesAdded} games, +${result.packagesAdded} packages, ${result.gamesUpdated}/${result.packagesUpdated} updated`,
        );
      } else {
        toast.error(
          `Sync finished with errors (${result.errors.length}). See history for details.`,
        );
      }
      setShowPreview(false);
      setDiff(emptyDiff());
      await Promise.all([loadStatus(), loadHistory()]);
    } catch (err) {
      console.error('[AdminGameSync] sync failed:', err);
      toast.error(
        `Sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setApplying(false);
    }
  }, [applying, loadStatus, loadHistory]);

  const handleSeedCompanies = useCallback(async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const r = await wepaySeedCompanies({ overwrite: false });
      toast.success(
        `Seeded companies — added ${r.added}, updated ${r.updated}, skipped ${r.skipped} (total ${r.total})`,
      );
      await Promise.all([loadStatus(), loadCompanies()]);
    } catch (err) {
      console.error('[AdminGameSync] seed failed:', err);
      toast.error(
        `Seed failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setSeeding(false);
    }
  }, [seeding, loadStatus, loadCompanies]);

  const handleMockSubmit = useCallback(async () => {
    if (mockSubmitting) return;
    if (!mockCompany) {
      toast.error('Select a company first');
      return;
    }
    const amountNum = Number(mockAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (!mockRef1) {
      toast.error('payToRef1 is required');
      return;
    }
    setMockSubmitting(true);
    setMockResult(null);
    try {
      const r = await wepayCreateMockTopup({
        payToCompany: mockCompany,
        payToAmount: amountNum,
        payToRef1: mockRef1,
        type: 'cashcard',
        autoCallback: mockAutoCallback,
        succeed: mockSucceed,
      });
      setMockResult(r);
      toast.success(`Mock top-up dispatched (destRef ${r.destRef})`);
    } catch (err) {
      console.error('[AdminGameSync] mock top-up failed:', err);
      toast.error(
        `Mock top-up failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setMockSubmitting(false);
    }
  }, [mockSubmitting, mockCompany, mockAmount, mockRef1, mockAutoCallback, mockSucceed]);

  const handleExportHistory = useCallback(() => {
    const csv = [
      [
        'StartedAt',
        'FinishedAt',
        'Status',
        'CompaniesProcessed',
        'GamesAdded',
        'GamesUpdated',
        'PackagesAdded',
        'PackagesUpdated',
        'DurationMs',
        'TriggeredBy',
        'Errors',
      ].join(','),
      ...history.map((run) =>
        [
          formatDate(run.startedAt),
          formatDate(run.finishedAt),
          run.success ? 'success' : 'failed',
          run.companiesProcessed,
          run.gamesAdded,
          run.gamesUpdated,
          run.packagesAdded,
          run.packagesUpdated,
          run.durationMs,
          run.triggeredBy,
          `"${(run.errors ?? []).join('; ').replace(/"/g, '""')}"`,
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wepay-sync-history-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [history]);

  // =====================================================================
  // Derived values
  // =====================================================================

  const pendingCount = useMemo(
    () => (diff ? diffPendingCount(diff) : 0),
    [diff],
  );

  const lastRun = status?.lastRun ?? history[0] ?? null;
  const isLoading = statusLoading || historyLoading;
  const showSeedButton = (status?.totalCompaniesConfigured ?? 0) === 0;

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-black">
              Game Sync
            </h1>
            <p className="font-mono text-xs text-black/60 mt-1 tracking-wider">
              Synchronise the catalog from wePAY into Firestore
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Pending updates badge — adopted from legacy PlusGame BOS */}
            <Button
              type="button"
              variant="outline"
              onClick={detectUpdates}
              disabled={previewing || isLoading}
              className="border-2 border-black rounded-none font-mono text-xs flex items-center gap-2"
            >
              {previewing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Bell size={14} />
              )}
              <span>อัพเดทข้อมูลเกม</span>
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold border-2 border-black ${pendingCount > 0 ? 'bg-red-500 text-white' : 'bg-white text-black'}`}
              >
                {pendingCount}
              </span>
            </Button>

            {showSeedButton && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSeedCompanies}
                disabled={seeding}
                className="border-2 border-black rounded-none font-mono text-xs"
              >
                {seeding ? (
                  <Loader2 size={14} className="mr-2 animate-spin" />
                ) : (
                  <Plus size={14} className="mr-2" />
                )}
                Seed Companies
              </Button>
            )}

            <Button
              type="button"
              onClick={handleSyncNow}
              disabled={previewing || applying || isLoading}
              className="bg-black text-white border-2 border-black rounded-none font-mono text-xs hover:bg-black/80"
            >
              {previewing || applying ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  {applying ? 'Syncing...' : 'Detecting...'}
                </>
              ) : (
                <>
                  <RefreshCw size={14} className="mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* API Status */}
          <div className="border-2 border-black bg-white">
            <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-widest uppercase">
                API Status
              </span>
              {status?.apiConfigured ? (
                <CheckCircle2 size={14} className="text-green-400" />
              ) : (
                <AlertCircle size={14} className="text-red-400" />
              )}
            </div>
            <div className="px-4 py-3">
              <div className="font-heading font-bold text-xl text-black">
                {status?.apiConfigured ? 'Configured' : 'Not Configured'}
              </div>
              <div className="font-mono text-[10px] text-black/50 mt-1 tracking-wider">
                Whitelisted IP {status?.whitelistedIp ?? '—'}
              </div>
            </div>
          </div>

          {/* Companies configured */}
          <div className="border-2 border-black bg-white">
            <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-widest uppercase">
                Companies
              </span>
              <RefreshCw size={14} />
            </div>
            <div className="px-4 py-3">
              <div className="font-heading font-bold text-xl text-black">
                {status?.totalCompaniesConfigured ?? 0}
              </div>
              <div className="font-mono text-[10px] text-black/50 mt-1 tracking-wider">
                wepay_companies
              </div>
            </div>
          </div>

          {/* Total Games */}
          <div className="border-2 border-black bg-white">
            <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-widest uppercase">
                Total Games
              </span>
              <RefreshCw size={14} />
            </div>
            <div className="px-4 py-3">
              <div className="font-heading font-bold text-xl text-black">
                {status?.totalGames ?? 0}
              </div>
              <div className="font-mono text-[10px] text-black/50 mt-1 tracking-wider">
                Synced from wePAY
              </div>
            </div>
          </div>

          {/* Last Sync */}
          <div className="border-2 border-black bg-white">
            <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-widest uppercase">
                Last Sync
              </span>
              <Clock size={14} />
            </div>
            <div className="px-4 py-3">
              <div className="font-heading font-bold text-base text-black">
                {lastRun
                  ? (() => {
                      const d = timestampToDate(lastRun.startedAt);
                      return d ? format(d, 'HH:mm:ss') : '—';
                    })()
                  : 'Never'}
              </div>
              <div className="font-mono text-[10px] text-black/50 mt-1 tracking-wider">
                {lastRun
                  ? (() => {
                      const d = timestampToDate(lastRun.startedAt);
                      return d ? format(d, 'MMM dd, yyyy') : '';
                    })()
                  : 'No sync history'}
              </div>
            </div>
          </div>
        </div>

        {/* Sync History */}
        <div className="border-2 border-black bg-white">
          <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
            <div>
              <div className="font-heading font-bold text-base">
                Sync History
              </div>
              <div className="font-mono text-[10px] text-black/50 tracking-wider">
                Recent synchronisation runs
              </div>
            </div>
            <Button
              type="button"
              onClick={handleExportHistory}
              variant="outline"
              size="sm"
              disabled={history.length === 0}
              className="border-2 border-black rounded-none font-mono text-xs"
            >
              <Download size={12} className="mr-1.5" />
              Export
            </Button>
          </div>

          {historyLoading ? (
            <div className="text-center py-10 font-mono text-xs text-black/40 tracking-wider">
              <Loader2 size={20} className="mx-auto animate-spin mb-2" />
              LOADING HISTORY...
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 font-mono text-xs text-black/50 tracking-wider">
              No sync history yet. Click "Sync Now" to start.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-black">
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Started
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Status
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Companies
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Games +/Δ
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Packages +/Δ
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Duration
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Triggered By
                    </TableHead>
                    <TableHead className="font-mono text-[10px] uppercase tracking-wider text-black">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((run) => (
                    <TableRow key={run.id} className="border-b border-black/20">
                      <TableCell className="font-mono text-xs text-black">
                        {formatDate(run.startedAt)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            run.success
                              ? 'bg-black text-white border-2 border-black rounded-none font-mono text-[10px]'
                              : 'bg-white text-red-600 border-2 border-red-600 rounded-none font-mono text-[10px]'
                          }
                        >
                          {run.success ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-black">
                        {run.companiesProcessed}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-black">
                        +{run.gamesAdded} / Δ{run.gamesUpdated}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-black">
                        +{run.packagesAdded} / Δ{run.packagesUpdated}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-black">
                        {formatDuration(run.durationMs)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-black">
                        {run.triggeredBy}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedRun(run)}
                          className="border-2 border-black rounded-none font-mono text-[10px]"
                        >
                          Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Mock Top-up panel */}
        <div className="border-2 border-black bg-white">
          <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
            <div>
              <div className="font-heading font-bold text-base">
                Test Mock Top-up
              </div>
              <div className="font-mono text-[10px] text-black/50 tracking-wider">
                Smoke-test the wePAY dispatch + callback pipeline (mock mode only)
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMockOpen((v) => !v)}
              className="border-2 border-black rounded-none font-mono text-xs"
            >
              <PlayCircle size={12} className="mr-1.5" />
              {mockOpen ? 'Hide' : 'Open'}
            </Button>
          </div>
          {mockOpen && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] tracking-wider uppercase">
                    Pay to Company
                  </Label>
                  <Select
                    value={mockCompany}
                    onValueChange={setMockCompany}
                    disabled={companies.length === 0}
                  >
                    <SelectTrigger className="border-2 border-black rounded-none font-mono text-xs">
                      <SelectValue
                        placeholder={
                          companies.length === 0
                            ? 'No companies — seed first'
                            : 'Choose a company'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => (
                        <SelectItem
                          key={c.code}
                          value={c.code}
                          className="font-mono text-xs"
                        >
                          {c.code} ({c.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] tracking-wider uppercase">
                    Amount (THB)
                  </Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="1"
                    value={mockAmount}
                    onChange={(e) => setMockAmount(e.target.value)}
                    className="border-2 border-black rounded-none font-mono text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] tracking-wider uppercase">
                    pay_to_ref1 (User ID)
                  </Label>
                  <Input
                    type="text"
                    value={mockRef1}
                    onChange={(e) => setMockRef1(e.target.value)}
                    className="border-2 border-black rounded-none font-mono text-xs"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono text-[10px] tracking-wider uppercase">
                    Options
                  </Label>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={mockAutoCallback}
                        onCheckedChange={setMockAutoCallback}
                      />
                      <span className="font-mono text-xs">autoCallback</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={mockSucceed}
                        onCheckedChange={setMockSucceed}
                      />
                      <span className="font-mono text-xs">succeed</span>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleMockSubmit}
                disabled={mockSubmitting}
                className="bg-black text-white border-2 border-black rounded-none font-mono text-xs"
              >
                {mockSubmitting ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Dispatching...
                  </>
                ) : (
                  'Dispatch Mock Top-up'
                )}
              </Button>

              {mockResult && (
                <div className="border-2 border-black bg-black/5 p-3 space-y-2">
                  <div className="font-mono text-[10px] tracking-wider uppercase text-black/50">
                    Result
                  </div>
                  <div className="font-mono text-xs">
                    <div>
                      <span className="text-black/50">destRef:</span>{' '}
                      {mockResult.destRef}
                    </div>
                    <div>
                      <span className="text-black/50">dispatch:</span>{' '}
                      {mockResult.dispatch.code}
                      {mockResult.dispatch.transaction_id != null
                        ? ` — txn ${mockResult.dispatch.transaction_id}`
                        : ''}
                      {mockResult.dispatch.desc
                        ? ` — ${mockResult.dispatch.desc}`
                        : ''}
                    </div>
                    {mockResult.callback && (
                      <div>
                        <span className="text-black/50">callback:</span> HTTP{' '}
                        {mockResult.callback.status} — {mockResult.callback.body}
                      </div>
                    )}
                    {mockResult.firestore && (
                      <div>
                        <span className="text-black/50">firestore:</span>{' '}
                        {mockResult.firestore.collection}/
                        {mockResult.firestore.id}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Configuration banner */}
        <div className="border-2 border-black bg-white p-3 flex items-start gap-3">
          <AlertCircle size={16} className="text-black mt-0.5 shrink-0" />
          <div className="font-mono text-xs text-black">
            <span className="font-bold">wePAY:</span> credentials are stored as
            Cloud Function secrets ({status?.apiConfigured ? 'configured' : 'missing'})
            · whitelisted IP{' '}
            <span className="font-bold">{status?.whitelistedIp ?? '—'}</span>
          </div>
        </div>
      </div>

      {/* Sync preview modal */}
      <SyncPreviewModal
        open={showPreview}
        onClose={() => !applying && setShowPreview(false)}
        diff={diff}
        onConfirm={handleApplySync}
        isApplying={applying}
        errors={previewErrors}
      />

      {/* Sync run details modal */}
      <Dialog
        open={selectedRun != null}
        onOpenChange={(v) => !v && setSelectedRun(null)}
      >
        <DialogContent className="max-w-md border-3 border-black rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight">
              Sync run details
            </DialogTitle>
            <DialogDescription className="font-mono text-xs text-black/50">
              {selectedRun ? formatDate(selectedRun.startedAt) : ''}
            </DialogDescription>
          </DialogHeader>
          {selectedRun && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Games Added" value={selectedRun.gamesAdded} />
                <Stat label="Games Updated" value={selectedRun.gamesUpdated} />
                <Stat
                  label="Packages Added"
                  value={selectedRun.packagesAdded}
                />
                <Stat
                  label="Packages Updated"
                  value={selectedRun.packagesUpdated}
                />
                <Stat
                  label="Companies Processed"
                  value={selectedRun.companiesProcessed}
                />
                <Stat
                  label="Duration"
                  value={formatDuration(selectedRun.durationMs)}
                />
              </div>

              {selectedRun.errors && selectedRun.errors.length > 0 && (
                <div className="border-2 border-red-600 bg-red-50 p-2">
                  <div className="font-mono text-[10px] tracking-wider uppercase text-red-600 mb-1">
                    Errors
                  </div>
                  <ul className="font-mono text-xs text-red-700 space-y-1">
                    {selectedRun.errors.map((e, i) => (
                      <li key={i}>• {e}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Badge
                className={
                  selectedRun.success
                    ? 'w-full justify-center bg-black text-white border-2 border-black rounded-none font-mono text-[10px] tracking-wider'
                    : 'w-full justify-center bg-white text-red-600 border-2 border-red-600 rounded-none font-mono text-[10px] tracking-wider'
                }
              >
                {selectedRun.success ? 'เปิดใช้งาน — Sync OK' : 'ปิดใช้งาน — Sync Failed'}
              </Badge>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: number | string;
}) {
  return (
    <div className="border-2 border-black p-2">
      <div className="font-mono text-[10px] tracking-wider uppercase text-black/50">
        {label}
      </div>
      <div className="font-heading font-bold text-lg text-black">{value}</div>
    </div>
  );
}
